import React from 'react';
import { FormView } from '../../UI/view/form-view';
import { FormItemType } from '../../UI/view/component-map';
import { initView } from '../../UI/view/view';
import { DataSource } from '../../UI/view/data-source';
import {
  composeBookingNotification,
  getBookingForEdit,
  getConsigneeDefaults,
  notifyBookingStatusOnly,
  updateBooking,
} from '../../../services/requests/bookings/bookings-requests';
import { bookingsPermissions } from './bookings-datasource';
import { applyConsigneeDefaults } from './bookings-consignee';
import { BookingAuditLog } from './bookings-audit-log';
import { BookingNotificationView, publishBookingNotification, useBookingNotification } from './bookings-notification';
import { legacyCancelRequestUrl, legacyPrintUrl } from './bookings-links';

const OTHER = 'Other...';
const OTHER_PORT = -1;

// Legacy RegularExpressionValidator: the first match must span the whole value.
const fullMatch = (value: string, pattern: RegExp) => {
  const m = pattern.exec(value);
  return !!m && m.index === 0 && m[0].length === value.length;
};

// Dropdown sources come back with the booking (GetBookingForEdit), so options reload once it is loaded.
const fromBooking = (map: (getValues: any) => any[]) => ({
  options: async (_data: any, getValues: any) => map(getValues),
  reloadOnFields: ['bookingId'],
});

const portOptions = (listName: string, withOther: boolean) => fromBooking((getValues) => [
  { id: 0, text: '-- Select --' },
  ...(withOther ? [{ id: OTHER_PORT, text: OTHER }] : []),
  ...(getValues(listName) ?? []).map((p: any) => ({ id: p.terminalLocationId, text: p.terminalName })),
]);

const text = (name: string, label: string, extra: any = {}) => ({ name, label, defaultValue: '', ...extra });
const date = (name: string, label: string, extra: any = {}) => ({ name, label, type: FormItemType.date, ...extra });
const readOnly = (name: string, label: string) => ({ name, label, type: FormItemType.labelField });
const row = (...items: any[]) => ({ type: FormItemType.row, items });

const portWithOther = (name: string, otherName: string, label: string, listName: string) => [
  {
    name,
    label,
    type: FormItemType.select,
    searchable: true,
    required: true,
    defaultValue: 0,
    zeroIsEmpty: true,
    ...portOptions(listName, true),
    onChange: ({ setValue }: any) => setValue(otherName, ''),
  },
  {
    name: otherName,
    label: `${label} (Other)`,
    maxLength: 100,
    hideIf: ({ watch }: any) => Number(watch?.(name)) !== OTHER_PORT,
    required: true,
  },
];

const legLabels = (leg: number) => [
  row(date(`sailingDateLeg${leg}`, `Sailing Date ${leg}`), date(`customerPortEtaLeg${leg}`, `Customer Port ETA ${leg}`)),
  row(text(`vesselNameLeg${leg}`, `Vessel Name ${leg}`), text(`voyageNumberLeg${leg}`, `Voyage Number ${leg}`)),
  row(text(`shipLineLeg${leg}`, `Ship Line ${leg}`), text(`sedNumberLeg${leg}`, `SED Number ${leg}`)),
];

const returnLegLabels = (leg: number) => [
  row(date(`customerReturnSailingDateLeg${leg}`, `Return Sailing Date ${leg}`), date(`customerReturnPortEtaLeg${leg}`, `Return Port ETA ${leg}`)),
  row(text(`customerReturnVesselNameLeg${leg}`, `Return Vessel Name ${leg}`), text(`customerReturnVoyageNumberLeg${leg}`, `Return Voyage Number ${leg}`)),
  row(text(`customerReturnShipLineLeg${leg}`, `Return Ship Line ${leg}`), text(`customerReturnSedNumberLeg${leg}`, `Return SED Number ${leg}`)),
];

const navigateTo = (url: string) => window.location.assign(url);

export const bookingsEdit = {
  pageHeader: 'Logistics - Bookings - Edit',
  editMode: true,
  disableDirtyCheck: true,
  permissions: bookingsPermissions,
  breadcrumbs: [{ name: 'Bookings', icon: 'calendar-check', to: '/bookings' }],
  items: [
    {
      type: FormItemType.fieldset,
      label: "Fields marked with '*' are required.",
      className: 'compact-fields',
      items: [
        readOnly('bookingId', 'Booking ID'),
        row(
          {
            name: 'customerId',
            label: 'Customer ID',
            type: FormItemType.select,
            searchable: true,
            required: true,
            defaultValue: 0,
            zeroIsEmpty: true,
            // setNewBookingCustomerIDEnabled: only new (Status 0), not cancel-requested bookings.
            disabled: ((watch: any) => !watch('customerEditable')) as any,
            ...fromBooking((getValues) => [
              { id: 0, text: '-- Select --' },
              ...(getValues('customers') ?? []).map((c: any) => ({ id: c.customerId, text: c.customerName })),
            ]),
            onChange: async ({ setValue, evt }: any) => {
              const customerId = parseInt(evt?.target?.value, 10);
              if (Number.isNaN(customerId)) return;
              applyConsigneeDefaults(await getConsigneeDefaults(customerId), setValue);
            },
          },
          {
            name: 'containerId',
            label: 'Container ID',
            type: FormItemType.select,
            searchable: true,
            defaultValue: '',
            ...fromBooking((getValues) => [
              { id: '', text: '-- Select --' },
              ...(getValues('containers') ?? []).map((c: string) => ({ id: c, text: c })),
            ]),
          },
          {
            name: 'fullEmptyType',
            label: 'Full/Empty',
            type: FormItemType.select,
            defaultValue: '',
            options: () => [{ id: '', text: '-- Select --' }, { id: 'Full', text: 'Full' }, { id: 'Empty', text: 'Empty' }],
          },
        ),
        row(
          text('poNumber', 'PO Number', { required: true }),
          {
            name: 'plantId',
            label: 'Sourcing Plant',
            type: FormItemType.select,
            required: true,
            defaultValue: '',
            options: () => [
              { id: '', text: '-- Select --' },
              { id: 'Alg', text: 'Algeria' },
              { id: 'Qat', text: 'Qatar' },
              { id: 'USA', text: 'USA' },
            ],
          },
        ),
        row(date('dateRequested', 'Date Requested', { required: true }), text('releaseNumber', 'Release Number')),
        row(date('earliestDeliveryDate', 'Earliest Delivery Date'), date('dateOrderReceived', 'Date Order Received')),
        text('remarks', 'Linde Remarks', { type: FormItemType.textarea }),
        row(
          {
            name: 'bookingAgentSelect',
            label: 'Booking Agent',
            type: FormItemType.select,
            searchable: true,
            required: true,
            defaultValue: '',
            ...fromBooking((getValues) => [
              { id: '', text: '--Please Select--' },
              ...(getValues('bookingAgents') ?? []).map((a: string) => ({ id: a, text: a })),
            ]),
            // OtherBookingAgentSelection: "Other..." clears the text box, other choices copy into it.
            onChange: ({ setValue, evt }: any) => {
              const value = evt?.target?.value;
              setValue('bookingAgentOther', value === OTHER ? '' : value ?? '');
            },
          },
          {
            name: 'bookingAgentOther',
            label: 'Booking Agent (Other)',
            maxLength: 100,
            required: true,
            hideIf: ({ watch }: any) => watch?.('bookingAgentSelect') !== OTHER,
            validation: {
              email: {
                message: 'The email is not well formed.',
                handler: (v: string) => !v || fullMatch(v, /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/),
              },
            },
          },
        ),
        row(
          ...portWithOther('usPort', 'usPortOther', 'Supply Plant Port', 'usPorts'),
          {
            name: 'containerSize',
            label: 'Cont/Module Size',
            type: FormItemType.select,
            defaultValue: '',
            options: () => [{ id: '', text: '-- Select --' }, { id: '20ft', text: '20ft' }, { id: '40ft', text: '40ft' }],
          },
        ),
        row(...portWithOther('customerPort', 'customerPortOther', 'Customer Port', 'customerPorts')),
        row(text('consigneeName', 'Consignee Name'), text('consigneeNumber', 'Consignee Number')),
        row(text('consigneeEmail', 'Consignee Email'), text('consigneePhone', 'Consignee Phone')),
        row(
          text('consigneeFax', 'Consignee Fax'),
          text('orderId', 'SO Number (Order #)', {
            validation: {
              length: { message: 'Length should be less than 11.', handler: (v: string) => !v || fullMatch(v, /.{0,11}$/) },
            },
          }),
        ),
        row(
          text('orderGuid', 'Order Guid', {
            validation: {
              guid: {
                message: 'Guid format should be {XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}',
                handler: (v: string) =>
                  !v || /^(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}$/.test(v),
              },
            },
          }),
          text('lindeOrderId', 'Order ID (new)'),
        ),
      ],
    },
    {
      type: FormItemType.fieldset,
      label: 'Following information will be filled by Booking Agent:',
      className: 'compact-fields',
      items: [
        row(text('bookNumber', 'Booking Number'), text('brokerRefNumber', 'Broker Reference Number')),
        row(text('portAddress1', 'Port Address1'), text('portAddress2', 'Port Address2')),
        row(text('portCity', 'Port City'), text('portState', 'Port State')),
        row(text('portZip', 'Port Zip'), text('portCountry', 'Port Country')),
        row(date('usPortCutoffDate', 'Supply Plant Port CutoffDate'), text('cutoffTime', 'Cutoff Time')),
        date('earliestReceivingDate', 'Earliest Receiving Date'),
        row(date('sailingDate', 'Sailing Date'), date('customerPortEta', 'Final Customer Port ETA')),
        row(text('vesselName', 'Vessel Name'), text('voyageNumber', 'Voyage Number')),
        row(text('shipLine', 'Ship Line'), text('sedNumber', 'SED Number')),
      ],
    },
    {
      type: FormItemType.fieldset,
      label: 'Additional Booking Legs noted here:',
      className: 'compact-fields',
      items: [
        ...legLabels(2),
        ...legLabels(3),
        text('freightForwarderRemarks', 'FreightForwarderRemarks', { type: FormItemType.textarea }),
        row(date('bookingCompletedDate', 'Booking Completed Date'), readOnly('agentNotifiedOn', 'Agent Notified On')),
      ],
    },
    {
      type: FormItemType.fieldset,
      label: 'Return Booking Information - Information will be filled by Booking Agent or Customer:',
      className: 'compact-fields',
      items: [
        text('customerReturnBookingNumber', 'Return Booking Number'),
        row(date('customerEndUseAtPlantDate', 'End Use At Plant Date'), date('customerDeparturePlantDate', 'Departure Plant Date')),
        date('customerOutgoingArrivalPortDate', 'Outgoing Arrival Port Date'),
        row(
          { name: 'customerDeparturePort', label: 'Departure Port', type: FormItemType.select, searchable: true, defaultValue: 0, ...portOptions('departurePorts', false) },
          date('customerDeparturePortDate', 'Departure Port Date'),
        ),
        row(
          { name: 'customerUsReturnPort', label: 'Return Port', type: FormItemType.select, searchable: true, defaultValue: 0, ...portOptions('returnPorts', false) },
          {
            name: 'customerReturnRegion',
            label: 'Return Region/Location',
            type: FormItemType.select,
            defaultValue: '',
            ...fromBooking((getValues) => (getValues('returnRegions') ?? []).map((r: any) => ({ id: r.value, text: r.text ?? r.value }))),
          },
        ),
        row(date('customerReturnSailingDate', 'Return Sailing Date'), date('customerUsReturnEta', 'Final Return Port ETA')),
        row(text('customerReturnVesselName', 'Return Vessel Name'), text('customerReturnVoyageNumber', 'Return Voyage Number')),
        row(text('customerReturnShipLine', 'Return Ship Line'), text('customerReturnSedNumber', 'Return SED Number')),
      ],
    },
    {
      type: FormItemType.fieldset,
      label: 'Additional Return Booking Legs noted here:',
      className: 'compact-fields',
      items: [
        ...returnLegLabels(2),
        ...returnLegLabels(3),
        text('customerComments', 'Return Comments', { type: FormItemType.textarea }),
        readOnly('customerBookingCompletedDate', 'Return Booking Completed On'),
        row(readOnly('lastUpdated', 'Last Updated On'), readOnly('lastUpdatedBy', 'Last Updated By')),
      ],
    },
    row(
      { type: FormItemType.submitButton, label: 'Save', permissions: bookingsPermissions },
      { type: FormItemType.button, label: 'Cancel', handler: ({ redirect }: any) => redirect('/bookings') },
      { type: FormItemType.button, label: 'Print', handler: ({ getValues }: any) => navigateTo(legacyPrintUrl(getValues('bookingId'))) },
    ),
    row(
      {
        type: FormItemType.button,
        label: 'Notify Agent',
        permissions: bookingsPermissions,
        // NotifyAgent_Click (CausesValidation) reads the current, possibly unsaved, form values.
        handler: async ({ handleSubmit }: any) => {
          await handleSubmit(async (values: any) => {
            publishBookingNotification(await composeBookingNotification(values.bookingId, values));
          })();
        },
      },
      {
        type: FormItemType.button,
        label: 'Cancel Booking',
        permissions: bookingsPermissions,
        handler: async ({ confirm, getValues }: any) => {
          if (await confirm('Are you sure you want to cancel the booking?')) navigateTo(legacyCancelRequestUrl(getValues('bookingId')));
        },
      },
      {
        type: FormItemType.button,
        label: 'Update Notify Status Only',
        permissions: bookingsPermissions,
        handler: async ({ confirm, getValues, methods }: any) => {
          if (!(await confirm('Are you sure you want to update the notify status without sending email?'))) return;
          await notifyBookingStatusOnly(getValues('bookingId'));
          await methods?.loadEntity?.();
        },
      },
    ),
    { type: FormItemType.customComponent, component: BookingAuditLog, name: 'auditLog' },
  ],
} as FormView;

const bookingsEditDS = {
  name: 'bookingsEdit',
  entityRequest: getBookingForEdit,
  // fvBooking_ItemUpdated -> NotifyAgent_Click: the save response is the mail view model.
  updateRequest: async (booking: any) => {
    const notification = await updateBooking(booking);
    publishBookingNotification(notification);
    return notification;
  },
  skipLocalUpdate: true,
  pk: 'bookingId',
} as DataSource;

const BookingsEditFormView = initView({ form: bookingsEdit, dataSource: bookingsEditDS });

// mvBooking: the edit view stays mounted (hidden) while the mail view is shown.
export const BookingsEditPage = () => {
  const notification = useBookingNotification();
  return (
    <>
      <div style={notification ? { display: 'none' } : undefined} className="h-100">
        <BookingsEditFormView />
      </div>
      {notification && <BookingNotificationView notification={notification} />}
    </>
  );
};
